/**
 * useCanvasJobWatch: poll one canvas job (`GET /canvas/{case_id}/jobs/{job_id}`)
 * every `intervalMs` until it ends, then call `onDone` once.
 *
 * R8: the effect owns a `cancelled` flag, so a slow answer for the previous
 * job (or case) never lands in state after the ids change or the page unmounts.
 * State is keyed by `caseId:jobId`, so the previous job's progress is never
 * shown for the next one.
 */
import { useEffect, useRef, useState } from 'react';
import { canvasJobService, type CanvasJobView } from '../../services/api';
import { JOB_POLL_MS, isTerminalJobStatus, watchCanvasJob } from './caseRuns';

export interface CanvasJobWatchOptions {
    intervalMs?: number;
    /** The job reached succeeded / failed / cancelled. */
    onDone?: (job: CanvasJobView) => void;
    /** The watch stopped without a terminal status (job not found, no access, repeated failures). */
    onLost?: (message: string) => void;
    /** Injected in tests; defaults to `canvasJobService.get`. */
    fetchJob?: (caseId: string, jobId: string) => Promise<CanvasJobView>;
}

export interface CanvasJobWatch {
    job: CanvasJobView | null;
    /** Last polling error (transient errors clear on the next good answer). */
    error: string | null;
    done: boolean;
}

interface WatchState { key: string; job: CanvasJobView | null; error: string | null }

export function useCanvasJobWatch(
    caseId: string | null | undefined,
    jobId: string | null | undefined,
    opts: CanvasJobWatchOptions = {},
): CanvasJobWatch {
    const key = caseId && jobId ? `${caseId}:${jobId}` : '';
    const [state, setState] = useState<WatchState>({ key: '', job: null, error: null });
    const intervalMs = opts.intervalMs ?? JOB_POLL_MS;

    // Latest callbacks without restarting the poll when a parent re-renders.
    const onDoneRef = useRef(opts.onDone);
    const onLostRef = useRef(opts.onLost);
    const fetchRef = useRef(opts.fetchJob);
    useEffect(() => {
        onDoneRef.current = opts.onDone;
        onLostRef.current = opts.onLost;
        fetchRef.current = opts.fetchJob;
    });

    useEffect(() => {
        if (!caseId || !jobId) return;
        const watchKey = `${caseId}:${jobId}`;
        let cancelled = false;
        const stop = watchCanvasJob({
            caseId,
            jobId,
            intervalMs,
            fetchJob: (c, j) => (fetchRef.current ?? canvasJobService.get)(c, j),
            onUpdate: (job) => {
                if (cancelled) return;
                setState({ key: watchKey, job, error: null });
            },
            onDone: (job) => {
                if (cancelled) return;
                onDoneRef.current?.(job);
            },
            onError: (message, fatal) => {
                if (cancelled) return;
                setState((prev) => ({ key: watchKey, job: prev.key === watchKey ? prev.job : null, error: message }));
                if (fatal) onLostRef.current?.(message);
            },
        });
        return () => { cancelled = true; stop(); };
    }, [caseId, jobId, intervalMs]);

    const current = state.key === key && key ? state : null;
    return {
        job: current?.job ?? null,
        error: current?.error ?? null,
        done: !!current?.job && isTerminalJobStatus(current.job.status),
    };
}
